<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import Button from '@uiKit/elements/Button.vue'
import Textarea from '@uiKit/elements/Textarea.vue'
import Message from '@uiKit/elements/Message.vue'
import Dialog from '@uiKit/elements/Dialog.vue'
import FormField from '@uiKit/components/FormField.vue'
import { formatDayMonthYear } from '@shared/utils'
import {
  declineReasonErrorOf,
  EXTENSION_DECLINE_REASON_MAX_LENGTH,
  isExtensionActionNeeded,
} from '../../utils/windowExtensionViewUtils'
import type { WindowExtensionProposal } from '../../types/propertyTypes'

// "Review extension" (FSD 3.3.2 item 8): the current and proposed end dates, the
// extra days, current funding — shown here and nowhere else on the seller's
// page — Sliceal's note, and how many owners have accepted.
//
// Accept signs the amended agreement with UAE PASS; the extension takes effect
// once every owner has. Decline needs a reason of 10–500 characters.
// Presentational — the container owns both actions.
const props = defineProps<{
  proposal: WindowExtensionProposal
  isAccepting: boolean
  isDeclining: boolean
  declineError: string
}>()

const open = defineModel<boolean>({ required: true })
const emit = defineEmits<{ accept: []; decline: [reason: string] }>()

const isDeclineMode = ref(false)
const reason = ref('')
const isReasonTouched = ref(false)

watch(open, (isOpen) => {
  if (!isOpen) return
  isDeclineMode.value = false
  reason.value = ''
  isReasonTouched.value = false
})

const canRespond = computed(() => isExtensionActionNeeded(props.proposal))
const isBusy = computed(() => props.isAccepting || props.isDeclining)
const reasonError = computed(() => declineReasonErrorOf(reason.value))

const facts = computed(() => {
  const { proposal } = props
  const funded = proposal.fundingProgressPercentage
  return [
    { label: 'Current end date', value: formatDayMonthYear(proposal.currentDeadlineAt) },
    { label: 'New end date', value: formatDayMonthYear(proposal.proposedDeadlineAt) },
    { label: 'Extra days', value: `+${proposal.extensionDays}` },
    { label: 'Funded so far', value: funded === null ? '—' : `${Math.round(funded)}%` },
    {
      label: 'Owners accepted',
      value: `${proposal.ownersAccepted} of ${proposal.ownersTotal}`,
    },
  ]
})

function submitDecline() {
  isReasonTouched.value = true
  if (reasonError.value) return
  emit('decline', reason.value.trim())
}

function close() {
  if (!isBusy.value) open.value = false
}
</script>

<template>
  <Dialog
    :model-value="open"
    header="Review extension"
    width="520px"
    :dismissable="!isBusy"
    @update:model-value="!$event && close()"
  >
    <Box direction="col" gap="4">
      <Text type="body" color="muted">
        Sliceal is proposing to extend your funding window by
        {{ proposal.extensionDays }} days, so your property has more time to be fully funded.
      </Text>

      <Box is="dl" class="extension__facts">
        <Box v-for="fact in facts" :key="fact.label" direction="col" gap="1">
          <Text is="dt" type="caption">{{ fact.label }}</Text>
          <Text is="dd" type="body" weight="semibold" class="extension__value">
            {{ fact.value }}
          </Text>
        </Box>
      </Box>

      <Box v-if="proposal.note" direction="col" gap="1" class="extension__note">
        <Text type="caption" weight="medium">Note from Sliceal</Text>
        <Text type="body-sm">{{ proposal.note }}</Text>
      </Box>

      <template v-if="canRespond">
        <FormField
          v-if="isDeclineMode"
          label="Why are you declining?"
          :error="isReasonTouched ? reasonError : ''"
          :helper-text="`${reason.trim().length} / ${EXTENSION_DECLINE_REASON_MAX_LENGTH} characters`"
        >
          <Textarea
            v-model="reason"
            :rows="4"
            placeholder="Let our team know why you’d rather not extend."
            :disabled="isDeclining"
            :invalid="isReasonTouched && !!reasonError"
            @blur="isReasonTouched = true"
          />
        </FormField>

        <Message v-if="declineError" color="danger" variant="outlined">{{ declineError }}</Message>

        <Text v-if="!isDeclineMode" type="caption" color="muted">
          Accepting signs the amended agreement with UAE PASS. The new end date applies once every
          owner has accepted.
        </Text>

        <Box flex gap="3" class="extension__actions">
          <template v-if="isDeclineMode">
            <Button
              label="Back"
              variant="outline"
              :disabled="isDeclining"
              @click="isDeclineMode = false"
            />
            <Button
              label="Decline extension"
              color="danger"
              :loading="isDeclining"
              @click="submitDecline"
            />
          </template>
          <template v-else>
            <Button
              label="Decline"
              variant="outline"
              color="secondary"
              :disabled="isAccepting"
              @click="isDeclineMode = true"
            />
            <Button label="Accept with UAE PASS" :loading="isAccepting" @click="emit('accept')" />
          </template>
        </Box>
      </template>

      <Box v-else flex justify="end">
        <Button label="Close" variant="outline" @click="close" />
      </Box>
    </Box>
  </Dialog>
</template>

<style scoped>
.extension__facts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
  margin: 0;
  padding: 1rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-md);
}

.extension__value {
  margin: 0;
  font-variant-numeric: tabular-nums;
}

.extension__note {
  padding: 0.75rem 1rem;
  border-left: 3px solid var(--p-primary-300);
  background: var(--p-primary-50);
  border-radius: var(--p-border-radius-sm);
}

.extension__actions {
  justify-content: flex-end;
  flex-wrap: wrap;
}
</style>
